import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../../api/client';

const { Title } = Typography;

const fmtNum = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : 0);

const Productos = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/productos', {
        params: { page, limit, search: searchDebounced || undefined },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar productos o servicios');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      posicionArancelaria: '',
      nombreProducto: '',
      derechoExportacion: 0,
      reintegro: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      posicionArancelaria: row.posicionArancelaria || '',
      nombreProducto: row.nombreProducto,
      derechoExportacion: fmtNum(row.derechoExportacion),
      reintegro: fmtNum(row.reintegro),
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      const payload = {
        posicionArancelaria: v.posicionArancelaria?.trim() || null,
        nombreProducto: v.nombreProducto.trim(),
        derechoExportacion: Number(v.derechoExportacion),
        reintegro: Number(v.reintegro),
      };
      if (editing) {
        await api.put(`/productos/${editing.id}`, payload);
        message.success('Producto o servicio actualizado');
      } else {
        await api.post('/productos', payload);
        message.success('Producto o servicio creado');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const handleDelete = (row) => {
    Modal.confirm({
      title: 'Eliminar producto o servicio',
      content: `¿Eliminar "${row.nombreProducto}"?`,
      okText: 'Eliminar',
      okType: 'danger',
      onOk: async () => {
        try {
          await api.delete(`/productos/${row.id}`);
          message.success('Producto o servicio eliminado');
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || 'Error al eliminar');
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64, responsive: ['sm'] },
    {
      title: 'Descripcion',
      dataIndex: 'posicionArancelaria',
      ellipsis: true,
      responsive: ['md'],
    },
    { title: 'Nombre del producto o servicio', dataIndex: 'nombreProducto', ellipsis: true },
    {
      title: 'Dcho. exportacion',
      dataIndex: 'derechoExportacion',
      width: 130,
      align: 'right',
      render: (val) => fmtNum(val).toLocaleString('es-AR', { maximumFractionDigits: 6 }),
      responsive: ['lg'],
    },
    {
      title: 'Reintegro',
      dataIndex: 'reintegro',
      width: 110,
      align: 'right',
      render: (val) => fmtNum(val).toLocaleString('es-AR', { maximumFractionDigits: 6 }),
      responsive: ['lg'],
    },
    {
      title: '',
      key: 'acciones',
      width: 88,
      fixed: isNarrow ? 'right' : undefined,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{ background: '#ffffff', borderColor: '#d8e8dc', maxWidth: '100%' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isNarrow ? 'column' : 'row',
            alignItems: isNarrow ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: 12,
            width: '100%',
          }}
        >
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Productos o Servicios
          </Title>
          <Space
            direction={isNarrow ? 'vertical' : 'horizontal'}
            style={{ width: isNarrow ? '100%' : 'auto' }}
            size="small"
          >
            <Input.Search
              allowClear
              placeholder="Buscar..."
              style={{ width: isNarrow ? '100%' : 220 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} block={isNarrow}>
              Nuevo
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} registros`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: isNarrow ? 360 : 960 }}
          size="small"
        />
      </Space>

      <Modal
        title={editing ? 'Editar producto o servicio' : 'Nuevo producto o servicio'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        destroyOnClose
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 520}
        centered
        styles={{ body: { maxHeight: 'min(70vh, 560px)', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 8 }}>
          <Form.Item name="posicionArancelaria" label="Descripcion">
            <Input placeholder="Ej: 1201.00.00" />
          </Form.Item>
          <Form.Item
            name="nombreProducto"
            label="Nombre del producto o servicio"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input placeholder="Descripcion del producto o servicio" />
          </Form.Item>
          <Form.Item
            name="derechoExportacion"
            label="Derecho de exportacion"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              step={0.000001}
              placeholder="0"
            />
          </Form.Item>
          <Form.Item name="reintegro" label="Reintegro" rules={[{ required: true, message: 'Requerido' }]}>
            <InputNumber style={{ width: '100%' }} step={0.000001} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Productos;
